/**
 * Helpers for recovering from GitHub SAML SSO credential-authorization gaps.
 *
 * Visiting GitHub's org SSO page only establishes a browser SAML session; it
 * does not retroactively authorize Blacksmith's already-issued OAuth token.
 * Credential authorizations for app tokens are created at app sign-in time,
 * so after authorizing SSO the user must go through a fresh (silent) OAuth
 * exchange. These helpers track that pending step across the tab switch to
 * GitHub and back, and trigger the silent re-login.
 */

const PENDING_KEY = "blacksmith_sso_reauth_pending";

/** Pending reauths older than this are considered abandoned. */
const PENDING_TTL_MS = 30 * 60 * 1000;

// sessionStorage is per-tab, so the native `storage` event never fires for
// same-tab writes; this event lets React surfaces track the flag instead.
const PENDING_CHANGE_EVENT = "blacksmith:sso-reauth-pending-change";

export interface PendingSsoReauth {
  orgs: string[];
  /** Write time; rewritten whenever another org is flagged. */
  at: number;
}

function notifyPendingChange(): void {
  window.dispatchEvent(new Event(PENDING_CHANGE_EVENT));
}

export function subscribeSsoReauthPending(onChange: () => void): () => void {
  window.addEventListener(PENDING_CHANGE_EVENT, onChange);
  return () => window.removeEventListener(PENDING_CHANGE_EVENT, onChange);
}

export function markSsoReauthPending(orgName: string): void {
  const existing = getPendingSsoReauth();
  const orgs = Array.from(new Set([...(existing?.orgs ?? []), orgName]));
  try {
    window.sessionStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ orgs, at: Date.now() } satisfies PendingSsoReauth),
    );
    notifyPendingChange();
  } catch {
    // Session storage unavailable (private mode quirks) - the user can still
    // recover via the error screen or a manual re-login.
  }
}

export function getPendingSsoReauth(): PendingSsoReauth | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSsoReauth;
    if (!Array.isArray(parsed.orgs) || typeof parsed.at !== "number") {
      return null;
    }
    if (Date.now() - parsed.at > PENDING_TTL_MS) {
      clearSsoReauthPending();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearSsoReauthPending(): void {
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
    notifyPendingChange();
  } catch {
    // ignore
  }
}

/**
 * Silently re-run the GitHub OAuth exchange and return to the current page.
 *
 * The Blacksmith OAuth app is already authorized, so GitHub redirects
 * straight back without a consent screen. The fresh sign-in creates a
 * credential authorization for every org the user holds an active SAML
 * session for, which is what actually restores /user/installations access.
 */
export function githubReconnectLogin(): void {
  clearSsoReauthPending();
  const redirect = encodeURIComponent(window.location.href);
  window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/login/github?redirect=${redirect}`;
}
